import { useState } from "react";
import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tag } from "@/types";
import { useDocumentTags, useTags } from "@/hooks/use-tags";

interface DocumentTagPickerProps {
  documentId: number;
}

export function DocumentTagPicker({ documentId }: DocumentTagPickerProps) {
  const { tags: allTags, createTagAsync, generateTagColor } = useTags();
  const { tags, isLoading, attachTag, detachTag, attachTagAsync } =
    useDocumentTags(documentId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const attachedIds = new Set(tags.map((t) => t.id));
  const suggestions = allTags.filter(
    (t) =>
      !attachedIds.has(t.id) &&
      t.name.toLowerCase().includes(draft.toLowerCase()),
  );

  const addByName = async (name: string) => {
    const trimmed = name.trim().replace(/^#/, "");
    if (!trimmed) return;

    const existing = allTags.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase(),
    );
    try {
      if (existing) {
        await attachTagAsync(existing.id);
      } else {
        const created = await createTagAsync({
          name: trimmed,
          color: generateTagColor(),
        });
        await attachTagAsync(created.id);
      }
      setDraft("");
      setOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {isLoading && (
          <span className="text-xs text-muted-foreground">Loading tags…</span>
        )}
        {tags.map((tag) => (
          <Badge
            key={tag.id}
            variant="secondary"
            className="flex items-center gap-1 pr-1"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: tag.color || "#94a3b8" }}
            />
            #{tag.name}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0"
              onClick={() => detachTag(tag.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="h-8">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add tag
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-64" align="start">
          <Command>
            <CommandInput
              placeholder="Find or create tag…"
              value={draft}
              onValueChange={setDraft}
            />
            <CommandList>
              <CommandEmpty>
                {draft.trim() ? (
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                    onClick={() => addByName(draft)}
                  >
                    Create #{draft.trim().replace(/^#/, "")}
                  </button>
                ) : (
                  "Type a tag name"
                )}
              </CommandEmpty>
              <CommandGroup>
                {suggestions.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={tag.name}
                    onSelect={() => {
                      attachTag(tag.id);
                      setOpen(false);
                      setDraft("");
                    }}
                  >
                    <span
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: tag.color || "#94a3b8" }}
                    />
                    #{tag.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {draft.trim() && (
            <div className="border-t p-2">
              <Button
                size="sm"
                className="w-full"
                onClick={() => addByName(draft)}
              >
                Create #{draft.trim().replace(/^#/, "")}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
